/**
 * BoardCard — the small, in-board visual for a single card.
 *
 * Used in three contexts:
 *   - "hand"      : 7rem wide × 10rem tall card in a hand row
 *   - "slot"      : equipment slot (CHAMP, EQUIP, DEFENSE, SIG, …) in the
 *                   player's equipment row. Same proportions as hand.
 *   - "prep"      : preparation phase (slightly smaller hand card)
 *
 * Renders portrait image (or fallback gradient), card type label, and name.
 * Optionally a small bottom strip with weight + attack-type pills.
 */
import { cn } from "@/lib/utils";
import type { Card } from "@/types/card";

const TYPE_BORDER: Record<string, string> = {
  Champion: "border-amber-600/40",
  Armor: "border-slate-500/40",
  Equipment: "border-slate-500/40",
  "Signature Move": "border-violet-500/40",
  Stance: "border-teal-500/40",
  Utility: "border-emerald-500/40",
  "Attack/Defend": "border-red-500/40",
  Token: "border-cyan-500/40",
};

const TYPE_FALLBACK_BG: Record<string, string> = {
  Champion: "bg-gradient-to-br from-amber-900/40 to-amber-700/20",
  Armor: "bg-gradient-to-br from-slate-800 to-slate-700",
  Equipment: "bg-gradient-to-br from-slate-800 to-slate-700",
  "Signature Move": "bg-gradient-to-br from-violet-900/40 to-violet-700/20",
  Stance: "bg-gradient-to-br from-teal-900/40 to-teal-700/20",
  Utility: "bg-gradient-to-br from-emerald-900/40 to-emerald-700/20",
  "Attack/Defend": "bg-gradient-to-br from-red-900/40 to-red-700/20",
  Token: "bg-gradient-to-br from-cyan-900/40 to-cyan-700/20",
};

export type BoardCardSize = "hand" | "slot" | "prep";

interface BoardCardProps {
  card: Card | null | undefined;
  size?: BoardCardSize;
  /** Header label shown above the image (CHAMP / EQUIP / DEFENSE / SIG…). */
  slotLabel?: string;
  /** Small badge in the top-right of the image (e.g. "STN" for stance). */
  cornerBadge?: string;
  /** Render an "▲" hint after the slot label, e.g. EQUIP ▲. */
  slotChevron?: boolean;
  /** Visible muting of the card (tapped / not playable). */
  muted?: boolean;
  /** Selected / active outline. */
  selected?: boolean;
  /** Click handler — when present, renders as a button. */
  onClick?: () => void;
  /** Make non-interactive in places like the equipment row. */
  className?: string;
  /** Hide the bottom weight/type pills (used for non-attack cards). */
  hidePills?: boolean;
  /** Pulse blue — used to highlight an armor card during the defender window. */
  blueHue?: boolean;
  /** Show a wear-counter badge (used on armor cards). 0 hides the badge. */
  wearCount?: number;
  /** Show a blood-counter badge (used on Bloodforged Plate). 0 hides the badge. */
  bloodCount?: number;
  /** Show a DISARMED indicator (used on weapon equip cards). */
  disarmed?: boolean;
}

const SIZE_CLASS: Record<BoardCardSize, string> = {
  // Smaller on mobile so a 5-7 card hand stays in a single row; grow on tablet+.
  hand: "w-[3.75rem] h-[5.25rem] sm:w-20 sm:h-28 md:w-24 md:h-32",
  // Equipment slots — NEVER use h-22 (Tailwind has no h-22). Use h-20.
  slot: "w-14 h-20 sm:w-20 sm:h-28 md:w-24 md:h-32",
  prep: "w-20 h-28 sm:w-24 sm:h-32",
};

export function BoardCard({
  card,
  size = "hand",
  slotLabel,
  cornerBadge,
  slotChevron,
  muted,
  selected,
  onClick,
  className,
  hidePills,
  blueHue,
  wearCount,
  bloodCount,
  disarmed,
}: BoardCardProps) {
  const type = card?.cardType ?? "";
  const borderClass = TYPE_BORDER[type] ?? "border-[#2a2f3a]";
  const fallbackBg = TYPE_FALLBACK_BG[type] ?? "bg-[#0f1115]";

  // Pull weight + attack-type for the bottom pills
  const weight =
    card?.attackDefendFields?.weight ??
    card?.signatureMoveFields?.attackWeight ??
    "";
  const attackType =
    card?.attackDefendFields?.attackType ??
    card?.attackDefendFields?.defendType ??
    card?.signatureMoveFields?.attackType ??
    "";

  const showPills =
    !hidePills && (size === "hand" || size === "prep") && (weight || attackType);

  const Wrapper = onClick ? "button" : "div";

  return (
    <div className={cn("flex flex-col items-stretch", className)}>
      {slotLabel && (
        <div className="mb-0.5 flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#64748b]">
          <span>{slotLabel}</span>
          {slotChevron && <span aria-hidden>▲</span>}
        </div>
      )}
      <Wrapper
        type={onClick ? "button" : undefined}
        onClick={onClick}
        className={cn(
          "relative overflow-hidden rounded-md border-2 text-left transition-all duration-150",
          SIZE_CLASS[size],
          borderClass,
          fallbackBg,
          onClick && "hover:scale-[1.03] hover:shadow-md hover:shadow-black/40 focus:outline-none focus:ring-2 focus:ring-amber/40",
          muted && "opacity-50",
          selected && "ring-2 ring-amber/70 border-amber/70",
          blueHue &&
            "!border-blue-400 ring-2 ring-blue-400/70 shadow-[0_0_18px_rgba(59,130,246,0.55)] animate-pulse",
        )}
      >
        {blueHue && (
          <span className="pointer-events-none absolute inset-0 z-[1] bg-blue-500/30" aria-hidden />
        )}
        {blueHue && (
          <span className="absolute right-1 top-1 z-[2] rounded bg-blue-500 px-1 py-px text-[8px] font-black uppercase tracking-wider text-white shadow">
            BLOCK
          </span>
        )}
        {/* image */}
        <div className="absolute inset-0">
          {card?.imageDataUrl ? (
            <img
              src={card.imageDataUrl}
              alt={card.name}
              className="h-full w-full object-cover"
              draggable={false}
            />
          ) : (
            <div className={cn("flex h-full w-full items-center justify-center", fallbackBg)}>
              <span className="text-3xl font-black text-white/10">
                {card?.cardType?.[0] ?? "?"}
              </span>
            </div>
          )}
        </div>

        {/* corner badge (e.g. STN) */}
        {cornerBadge && (
          <span className="absolute left-1 top-1 rounded bg-teal-500/80 px-1 py-px text-[9px] font-bold text-white">
            {cornerBadge}
          </span>
        )}

        {/* wear-counter badge — armor cards take damage cumulatively. */}
        {typeof wearCount === "number" && wearCount > 0 && (
          <span
            className="absolute right-1 top-1 z-[3] flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-black uppercase text-white shadow ring-1 ring-orange-200/60"
            title={`${wearCount} wear counter${wearCount === 1 ? "" : "s"}`}
          >
            {wearCount}
          </span>
        )}
        {/* blood-counter badge — shown when Bloodforged Plate is equipped. */}
        {typeof bloodCount === "number" && bloodCount > 0 && (
          <span
            className="absolute right-1 top-7 z-[3] flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-black uppercase text-white shadow ring-1 ring-red-200/60"
            title={`${bloodCount} blood counter${bloodCount === 1 ? "" : "s"}`}
  >
           {bloodCount}
          </span>
        )}

        {/* DISARMED indicator — weapon equip slot has been disarmed. */}
        {disarmed && (
          <>
            <span
              className="pointer-events-none absolute inset-0 z-[2] bg-red-900/45"
              aria-hidden
            />
            <span className="absolute inset-x-0 top-1/2 z-[3] -translate-y-1/2 px-1 text-center">
              <span className="rounded bg-red-600 px-1 py-px text-[9px] font-black uppercase tracking-wider text-white shadow">
                Disarmed
              </span>
            </span>
          </>
        )}

        {/* bottom info strip */}
        <div className="absolute inset-x-0 bottom-0 bg-black/75 px-1 py-0.5 sm:px-1.5 sm:py-1">
          <p className="line-clamp-2 text-[8px] sm:text-[10px] md:text-[11px] font-semibold leading-tight text-white">
            {card?.name ?? "Unknown"}
          </p>
          {showPills && (
            <div className="mt-0.5 hidden sm:flex gap-0.5">
              {weight && (
                <span className="rounded bg-amber-500/20 px-1 py-px text-[8px] uppercase tracking-wide text-amber-300">
                  {weight}
                </span>
              )}
              {attackType && (
                <span className="rounded bg-blue-500/20 px-1 py-px text-[8px] uppercase tracking-wide text-blue-300">
                  {attackType}
                </span>
              )}
            </div>
          )}
        </div>
      </Wrapper>
    </div>
  );
}
